import React, { useState } from 'react';
import './InsightCard.css';

const InsightCard = ({ insight }) => {
  const [showRaw, setShowRaw] = useState(false);
  
  // 한글 포맷팅된 데이터가 있으면 사용, 없으면 원본 사용
  const koreanData = insight.korean_format;
  const hasKoreanFormat = koreanData && koreanData.type !== 'error';

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Unknown') {
      return 'Unknown';
    }
    
    try {
      if (typeof dateString === 'string' && dateString.includes('KST')) {
        return dateString;
      }
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const renderKoreanInsight = () => {
    if (!koreanData && typeof insight.analysis === 'string' && insight.analysis.trim()) {
      return (
        <div className="korean-insight">
          <div className="insight-text-report">{insight.analysis}</div>
        </div>
      );
    }

    if (!hasKoreanFormat) {
      return (
        <div className="korean-insight-error">
          <h4>❌ 포맷팅 오류</h4>
          <p>{koreanData?.error || '한글 포맷팅을 사용할 수 없습니다.'}</p>
        </div>
      );
    }

    return (
      <div className="korean-insight">
        <div className="insight-header-section">
          <div className="insight-meta">
            {koreanData.data_period && (
              <span className="data-period">📅 분석 기간: {koreanData.data_period}</span>
            )}
            {koreanData.confidence_score !== undefined && (
              <span className="confidence-score">
                🎯 신뢰도: {(koreanData.confidence_score * 100).toFixed(1)}%
              </span>
            )}
          </div>
        </div>

        {/* 사용자 행동 분석 */}
        {koreanData.type === 'user_behavior' && (
          <div className="user-behavior-insight">
            {koreanData.patterns && (
              <div className="insight-section">
                <h4>{koreanData.patterns.title}</h4>
                <ul>
                  {koreanData.patterns.items.map((pattern, index) => (
                    <li key={index}>{pattern}</li>
                  ))}
                </ul>
              </div>
            )}

            {koreanData.insights && (
              <div className="insight-section">
                <h4>{koreanData.insights.title}</h4>
                <ul>
                  {koreanData.insights.items.map((insight, index) => (
                    <li key={index}>{insight}</li>
                  ))}
                </ul>
              </div>
            )}

            {koreanData.opportunities && (
              <div className="insight-section">
                <h4>{koreanData.opportunities.title}</h4>
                <ul>
                  {koreanData.opportunities.items.map((opportunity, index) => (
                    <li key={index}>{opportunity}</li>
                  ))}
                </ul>
              </div>
            )}

            {koreanData.recommendations && (
              <div className="insight-section">
                <h4>{koreanData.recommendations.title}</h4>
                <div className="recommendations-list">
                  {koreanData.recommendations.items.map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <div className="recommendation-action">{rec.action}</div>
                      <div className="recommendation-meta">
                        <span className="priority">{rec.priority}</span>
                        <span className="effort">구현 난이도: {rec.implementation_effort}</span>
                      </div>
                      {rec.expected_impact && (
                        <div className="expected-impact">예상 효과: {rec.expected_impact}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 비용 최적화 분석 */}
        {koreanData.type === 'cost_optimization' && (
          <div className="cost-optimization-insight">
            {koreanData.cost_analysis && (
              <div className="insight-section">
                <h4>{koreanData.cost_analysis.title}</h4>
                <div className="cost-metrics">
                  <div className="cost-metric">{koreanData.cost_analysis.monthly_cost}</div>
                  <div className="cost-metric">{koreanData.cost_analysis.most_expensive}</div>
                  <div className="cost-metric">{koreanData.cost_analysis.most_efficient}</div>
                  <div className="cost-metric">{koreanData.cost_analysis.cost_per_conversion}</div>
                </div>
              </div>
            )}

            {koreanData.optimization_opportunities && (
              <div className="insight-section">
                <h4>{koreanData.optimization_opportunities.title}</h4>
                <div className="opportunities-list">
                  {koreanData.optimization_opportunities.items.map((opp, index) => (
                    <div key={index} className="opportunity-item">
                      <div className="opportunity-text">{opp.opportunity}</div>
                      <div className="opportunity-meta">
                        <span className="savings">절약 가능: {opp.potential_savings}</span>
                        <span className="complexity">복잡도: {opp.implementation_complexity}</span>
                        <span className="risk">위험도: {opp.risk_level}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {koreanData.recommendations && (
              <div className="insight-section">
                <h4>{koreanData.recommendations.title}</h4>
                <div className="recommendations-list">
                  {koreanData.recommendations.items.map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <div className="recommendation-action">{rec.action}</div>
                      <div className="recommendation-meta">
                        <span className="priority">{rec.priority}</span>
                        <span className="savings">예상 절약: {rec.estimated_savings}</span>
                        <span className="time">소요 시간: {rec.implementation_time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 전환율 분석 */}
        {koreanData.type === 'conversion_analysis' && (
          <div className="conversion-analysis-insight">
            {koreanData.funnel_analysis && (
              <div className="insight-section">
                <h4>{koreanData.funnel_analysis.title}</h4>
                <div className="funnel-metrics">
                  <div className="funnel-metric">{koreanData.funnel_analysis.strongest_stage}</div>
                  <div className="funnel-metric">{koreanData.funnel_analysis.weakest_stage}</div>
                  <div className="funnel-metric">{koreanData.funnel_analysis.overall_health}</div>
                  <div className="funnel-metric">{koreanData.funnel_analysis.benchmark}</div>
                </div>
              </div>
            )}

            {koreanData.drop_off_analysis && (
              <div className="insight-section">
                <h4>{koreanData.drop_off_analysis.title}</h4>
                <div className="drop-off-list">
                  {koreanData.drop_off_analysis.items.map((dropOff, index) => (
                    <div key={index} className="drop-off-item">
                      <div className="drop-off-stage">{dropOff.stage}</div>
                      <div className="drop-off-rate">이탈률: {dropOff.drop_off_rate}</div>
                      <div className="drop-off-causes">
                        <strong>주요 원인:</strong>
                        <ul>
                          {dropOff.likely_causes.map((cause, causeIndex) => (
                            <li key={causeIndex}>{cause}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="impact-level">영향도: {dropOff.impact_level}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {koreanData.optimization_strategies && (
              <div className="insight-section">
                <h4>{koreanData.optimization_strategies.title}</h4>
                <div className="strategies-list">
                  {koreanData.optimization_strategies.items.map((strategy, index) => (
                    <div key={index} className="strategy-item">
                      <div className="strategy-text">{strategy.strategy}</div>
                      <div className="strategy-meta">
                        <span className="target">대상: {strategy.target_stage}</span>
                        <span className="improvement">예상 개선: {strategy.expected_improvement}</span>
                        <span className="priority">{strategy.priority}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {koreanData.ab_test_recommendations && (
              <div className="insight-section">
                <h4>{koreanData.ab_test_recommendations.title}</h4>
                <div className="ab-tests-list">
                  {koreanData.ab_test_recommendations.items.map((test, index) => (
                    <div key={index} className="ab-test-item">
                      <div className="test-name">{test.test_name}</div>
                      <div className="test-hypothesis">가설: {test.hypothesis}</div>
                      <div className="test-metric">성공 지표: {test.success_metric}</div>
                      <div className="test-duration">예상 기간: {test.estimated_duration}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 지역 분석 */}
        {koreanData.type === 'geographic_analysis' && (
          <div className="geographic-analysis-insight">
            <div className="insight-summary">{koreanData.summary}</div>
            
            {koreanData.top_markets && (
              <div className="insight-section">
                <h4>{koreanData.top_markets.title}</h4>
                <ul>
                  {koreanData.top_markets.items.map((market, index) => (
                    <li key={index}>{market}</li>
                  ))}
                </ul>
              </div>
            )}

            {koreanData.expansion_opportunities && (
              <div className="insight-section">
                <h4>{koreanData.expansion_opportunities.title}</h4>
                <ul>
                  {koreanData.expansion_opportunities.items.map((opportunity, index) => (
                    <li key={index}>{opportunity}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* 성능 분석 */}
        {koreanData.type === 'performance_analysis' && (
          <div className="performance-analysis-insight">
            {koreanData.peak_hours && (
              <div className="insight-section">
                <h4>{koreanData.peak_hours.title}</h4>
                <ul>
                  {koreanData.peak_hours.items.map((hour, index) => (
                    <li key={index}>{hour}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRawInsight = () => {
    try {
      const analysis = typeof insight.analysis === 'string' 
        ? JSON.parse(insight.analysis) 
        : insight.analysis;
      
      return (
        <div className="raw-insight">
          <pre>{JSON.stringify(analysis, null, 2)}</pre>
        </div>
      );
    } catch (error) {
      return (
        <div className="raw-insight">
          <pre>{insight.analysis || 'No analysis data available'}</pre>
        </div>
      );
    }
  };

  return (
    <div className="insight-card">
      <div className="insight-header">
        <div className="insight-title-section">
          <h4>{hasKoreanFormat ? koreanData.title : insight.insight_type.replace('_', ' ').toUpperCase()}</h4>
          <span className="insight-date">{formatDate(insight.created_at)}</span>
        </div>
        <div className="insight-actions">
          <button 
            onClick={() => setShowRaw(!showRaw)} 
            className={`view-toggle ${showRaw ? 'active' : ''}`}
            title={showRaw ? '한글 분석 보기' : '원본 데이터 보기'}
          >
            {showRaw ? '📊 한글 분석' : '🔍 원본 데이터'}
          </button>
        </div>
      </div>
      
      <div className="insight-content">
        {showRaw ? renderRawInsight() : renderKoreanInsight()}
      </div>
    </div>
  );
};

export default InsightCard;
